import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Heart, Building2, MapPin, Phone, Gift,
  DollarSign, Users, Calendar, Handshake, Camera, X, Plus,
  Loader2, Info, AlertCircle, CheckCircle, ChevronDown, Trash2,
  BarChart3, Image as ImageIcon, FileText, Shield
} from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import ImageUploader from '../components/ImageUploader';
import { OrgAtividadesTab } from '../components/OrgAtividadesTab';
import TransferirResponsavelCard from '../components/TransferirResponsavelCard';
import ConvitesPendentesCard from '../components/ConvitesPendentesCard';
import { resolveBackendImage } from '../utils/brandLogo';


// Tabs
const TABS = [
  { id: 'info', label: 'Informações', icon: Building2 },
  { id: 'atividades', label: 'Eventos e Ações', icon: Calendar },
  { id: 'support', label: 'Como Apoiar', icon: Heart },
  { id: 'impact', label: 'Impacto', icon: BarChart3 },
  { id: 'governance', label: 'Governança', icon: Shield },
  { id: 'media', label: 'Mídia', icon: ImageIcon }
];

// Impact ranges
const IMPACT_RANGES_MONTH = [
  { value: '1-10', label: '1 a 10 pessoas' },
  { value: '11-50', label: '11 a 50 pessoas' },
  { value: '51-200', label: '51 a 200 pessoas' },
  { value: '201-500', label: '201 a 500 pessoas' },
  { value: '500+', label: 'Mais de 500 pessoas' }
];

const IMPACT_RANGES_YEAR = [
  { value: '1-50', label: '1 a 50 pessoas' },
  { value: '51-200', label: '51 a 200 pessoas' },
  { value: '201-1000', label: '201 a 1000 pessoas' },
  { value: '1000+', label: 'Mais de 1000 pessoas' }
];

// Public Support Types
const PUBLIC_SUPPORT_TYPES = [
  { value: 'financeiro', label: 'Financeiro / Subvenção' },
  { value: 'cessao_espaco', label: 'Cessão de Espaço' },
  { value: 'materiais', label: 'Materiais / Insumos' },
  { value: 'equipe_apoio', label: 'Equipe / Servidor de Apoio' },
  { value: 'parceria_acoes', label: 'Parceria em Ações' }
];

/**
 * Extrai o ID de um vídeo do YouTube a partir de qualquer URL comum.
 * Retorna null se não for um link válido.
 */
export function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{6,})/i,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}

// Secretaries (common ones)
const SECRETARIES = [
  { value: 'assistencia_social', label: 'Assistência Social' },
  { value: 'cultura', label: 'Cultura' },
  { value: 'educacao', label: 'Educação' },
  { value: 'saude', label: 'Saúde' },
  { value: 'meio_ambiente', label: 'Meio Ambiente' },
  { value: 'esporte_lazer', label: 'Esporte e Lazer' },
  { value: 'desenvolvimento_economico', label: 'Desenvolvimento Econômico' },
  { value: 'governo', label: 'Governo / Gabinete' },
  { value: 'outra', label: 'Outra' }
];

// Regularization Checklist Items
const REGULARIZATION_CHECKLIST_ITEMS = [
  { key: 'cnpj', label: 'CNPJ ativo e regular' },
  { key: 'estatuto', label: 'Estatuto Social atualizado' },
  { key: 'ata', label: 'Ata de eleição da diretoria' },
  { key: 'diretoria', label: 'Diretoria vigente' },
  { key: 'conselho', label: 'Inscrição em conselho (quando aplicável)' },
  { key: 'certidoes', label: 'Certidões negativas em dia' }
];

// Components
const FormField = ({ label, required, children, hint }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const SelectField = ({ value, onChange, options, placeholder, ...props }) => (
  <div className="relative">
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 appearance-none bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
      {...props}
    >
      <option value="">{placeholder || 'Selecione...'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
  </div>
);

const ToggleSwitch = ({ enabled, onChange, label, description }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-purple-300 transition-all">
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-purple-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'left-7' : 'left-1'
        }`}
      />
    </button>
    <div className="flex-1">
      <p className="font-medium text-gray-900">{label}</p>
      {description && <p className="text-sm text-gray-500">{description}</p>}
    </div>
  </div>
);

// Support Option Card
const SupportOptionCard = ({ 
  icon: Icon, 
  title, 
  color, 
  enabled, 
  onToggle, 
  children 
}) => (
  <div className={`rounded-xl border-2 transition-all ${
    enabled ? `border-${color}-300 bg-${color}-50` : 'border-gray-200 bg-white'
  }`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full p-4 flex items-center gap-3"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
        enabled ? `bg-${color}-500 text-white` : 'bg-gray-100 text-gray-400'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="font-medium flex-1 text-left">{title}</span>
      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
        enabled ? `border-${color}-500 bg-${color}-500 text-white` : 'border-gray-300'
      }`}>
        {enabled && <CheckCircle className="w-4 h-4" />}
      </div>
    </button>
    {enabled && children && (
      <div className="px-4 pb-4 border-t border-gray-100 pt-4">
        {children}
      </div>
    )}
  </div>
);

// ============================================================================
// ParceirosSection — CRUD de empresas parceiras (reaproveita `supporters` com
// supporter_type='empresa'). Persiste inline (sem passar pelo botão Salvar).
// ============================================================================
function ParceirosSection({ orgId, municipioId, initialItems = [] }) {
  const [items, setItems] = useState(initialItems);
  const [nome, setNome] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const handleFile = (f) => {
    if (!f) { setFile(null); setPreviewUrl(''); return; }
    if (f.type && f.type.includes('svg')) {
      toast.error('SVG não é aceito. Use PNG, JPG ou WebP.');
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const addParceiro = async () => {
    if (!nome.trim()) { toast.error('Informe o nome do parceiro.'); return; }
    if (!file) { toast.error('Selecione a logo do parceiro.'); return; }
    setSaving(true);
    try {
      // 1. Upload da logo (usa mesmo endpoint padrão da plataforma)
      // Fallback do municipio_id: se a prop chegou vazia (algumas orgs
      // não têm municipio_id no state), busca via /auth/me. Alinha com o
      // ImageUploader — evita 422 "municipio_id missing" em produção.
      let munIdEff = municipioId || '';
      if (!munIdEff) {
        try {
          const meRes = await api.get('/auth/me');
          munIdEff = meRes?.data?.municipio_id || '';
        } catch (_) { /* segue e deixa o backend errar com msg clara */ }
      }
      const fd = new FormData();
      fd.append('file', file, file.name || 'logo.png');
      fd.append('municipio_id', munIdEff);
      fd.append('entidade_id', orgId);
      fd.append('area', 'ong');
      fd.append('tipo', 'logo');
      const up = await api.post('/upload/imagem', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const logoUrl = up.data?.urls?.medium || up.data?.urls?.large;

      // 2. Cria o supporter (tipo=empresa)
      const resp = await api.post(`/org-projects/${orgId}/supporters`, {
        name: nome.trim(),
        supporter_type: 'empresa',
        logo_url: logoUrl,
        order_index: items.length,
      });
      setItems((prev) => [...prev, resp.data]);
      setNome(''); setFile(null); setPreviewUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Parceiro adicionado.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao adicionar parceiro.');
    } finally {
      setSaving(false);
    }
  };

  const removeParceiro = async (id) => {
    if (!window.confirm('Remover este parceiro?')) return;
    try {
      await api.delete(`/supporters/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success('Parceiro removido.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao remover.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6" data-testid="parceiros-section">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
        <Handshake className="w-5 h-5 text-purple-600" />
        Empresas parceiras
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Adicione empresas que apoiam ou patrocinam esta organização. Os logos
        aparecerão no perfil público na seção &quot;Parceiros&quot;.
      </p>

      {/* Lista atual */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
          {items.map((p) => (
            <div
              key={p.id}
              className="relative group border border-gray-200 rounded-xl p-3 flex flex-col items-center gap-2 bg-slate-50"
              data-testid={`parceiro-item-${p.id}`}
            >
              <div className="w-full aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center">
                {p.logo_url ? (
                  <img
                    src={resolveBackendImage(p.logo_url)}
                    alt={p.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <p className="text-xs text-gray-700 text-center truncate w-full">{p.name}</p>
              <button
                type="button"
                onClick={() => removeParceiro(p.id)}
                className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remover"
                data-testid={`btn-remove-parceiro-${p.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Adicionar novo */}
      <div className="border-t pt-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Adicionar parceiro</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nome (alt text)</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Padaria do Zé"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              data-testid="input-parceiro-nome"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Logo (PNG/JPG/WebP)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="w-full text-sm"
              data-testid="input-parceiro-file"
            />
            {previewUrl && (
              <div className="mt-2 w-16 h-16 rounded border overflow-hidden bg-white">
                <img src={previewUrl} alt="preview" className="w-full h-full object-contain" />
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={addParceiro}
          disabled={saving}
          className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
          data-testid="btn-add-parceiro"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Adicionar
        </button>
      </div>
    </div>
  );
}


export default function EditarOrganizacao() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const initialTab = searchParams.get('tab') || 'info';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [convitesRefreshTick, setConvitesRefreshTick] = useState(0);
  const [org, setOrg] = useState(null);
  const [municipio, setMunicipio] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    long_description: '',
    whatsapp: '',
    is_whatsapp: false,
    email: '',
    instagram: '',
    website: '',
    video_institucional_url: '',
    support_options: {
      donate_items: { enabled: false, items_accepted: '', instructions: '' },
      donate_money: { enabled: false, instructions: '', link: '' },
      volunteer: { enabled: false, instructions: '', areas_needed: [] },
      participate: { enabled: false, instructions: '', link: '' },
      partnership: { enabled: false, contact_whatsapp: '', contact_email: '' }
    },
    impact_metrics: {
      people_impacted_last_month: '',
      people_impacted_last_year: '',
      families_impacted: '',
      services_delivered: '',
      measurement_method: ''
    },
    avatar_url: '',
    gallery: [],
    imagens: {},
    municipio_id: '',
    // Governance fields
    regularization_checklist: {
      cnpj: false,
      estatuto: false,
      ata: false,
      diretoria: false,
      conselho: false,
      certidoes: false
    },
    receives_public_support: false,
    public_support_types: [],
    public_support_secretary: '',
    has_active_agreement: false,
    agreement_period_start: '',
    agreement_period_end: ''
  });

  const loadOrg = useCallback(async () => {
    try {
      const res = await api.get(`/org-projects/${orgId}`);
      setOrg(res.data);
      
      // Load municipality info if available
      if (res.data.municipio_id) {
        try {
          const municipiosRes = await api.get('/municipios');
          const found = municipiosRes.data.find(m => m.id === res.data.municipio_id);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
      
      // Populate form
      setFormData({
        name: res.data.name || '',
        short_description: res.data.short_description || '',
        long_description: res.data.long_description || '',
        whatsapp: res.data.whatsapp || '',
        is_whatsapp: !!res.data.is_whatsapp,
        email: res.data.email || '',
        instagram: res.data.instagram || '',
        website: res.data.website || '',
        video_institucional_url: res.data.video_institucional_url || '',
        support_options: {
          donate_items: res.data.support_options?.donate_items || { enabled: false, items_accepted: '', instructions: '' },
          donate_money: res.data.support_options?.donate_money || { enabled: false, instructions: '', link: '' },
          volunteer: res.data.support_options?.volunteer || { enabled: false, instructions: '', areas_needed: [] },
          participate: res.data.support_options?.participate || { enabled: false, instructions: '', link: '' },
          partnership: res.data.support_options?.partnership || { enabled: false, contact_whatsapp: '', contact_email: '' }
        },
        impact_metrics: res.data.impact_metrics || {
          people_impacted_last_month: '',
          people_impacted_last_year: '',
          families_impacted: '',
          services_delivered: '',
          measurement_method: ''
        },
        avatar_url: res.data.avatar_url || '',
        gallery: res.data.gallery || [],
        imagens: res.data.imagens || {},
        municipio_id: res.data.municipio_id || '',
        // Governance fields
        regularization_checklist: res.data.regularization_checklist || {
          cnpj: false,
          estatuto: false,
          ata: false,
          diretoria: false,
          conselho: false,
          certidoes: false
        },
        receives_public_support: res.data.receives_public_support || false,
        public_support_types: res.data.public_support_types || [],
        public_support_secretary: res.data.public_support_secretary || '',
        has_active_agreement: res.data.has_active_agreement || false,
        agreement_period_start: res.data.agreement_period_start || '',
        agreement_period_end: res.data.agreement_period_end || ''
      });
    } catch (error) {
      console.error('Erro ao carregar organização:', error);
      toast.error('Organização não encontrada');
      navigate('/social');
    } finally {
      setLoading(false);
    }
  }, [orgId, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrg();
    }
  }, [isAuthenticated, loadOrg]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateSupportOption = (option, field, value) => {
    setFormData(prev => ({
      ...prev,
      support_options: {
        ...prev.support_options,
        [option]: {
          ...prev.support_options[option],
          [field]: value
        }
      }
    }));
  };

  const toggleSupportOption = (option) => {
    updateSupportOption(option, 'enabled', !formData.support_options[option].enabled);
  };

  const updateImpactMetric = (field, value) => {
    setFormData(prev => ({
      ...prev,
      impact_metrics: {
        ...prev.impact_metrics,
        [field]: value
      }
    }));
  };

  const updateChecklistItem = (key, value) => {
    setFormData(prev => ({
      ...prev,
      regularization_checklist: {
        ...prev.regularization_checklist,
        [key]: value
      }
    }));
  };

  const togglePublicSupportType = (type) => {
    setFormData(prev => {
      const current = prev.public_support_types || [];
      const newTypes = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return { ...prev, public_support_types: newTypes };
    });
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      
      if (type === 'avatar') {
        updateField('avatar_url', base64);
      } else if (type === 'gallery') {
        if (formData.gallery.length >= 10) {
          toast.error('Máximo de 10 imagens na galeria');
          return;
        }
        updateField('gallery', [...formData.gallery, base64]);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeGalleryImage = (index) => {
    const newGallery = formData.gallery.filter((_, i) => i !== index);
    updateField('gallery', newGallery);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        name: formData.name,
        short_description: formData.short_description,
        long_description: formData.long_description,
        whatsapp: formData.whatsapp,
        is_whatsapp: formData.is_whatsapp,
        email: formData.email,
        instagram: formData.instagram,
        website: formData.website,
        video_institucional_url: formData.video_institucional_url,
        support_options: formData.support_options,
        impact_metrics: formData.impact_metrics,
        avatar_url: formData.avatar_url,
        gallery: formData.gallery,
        // Governance fields
        regularization_checklist: formData.regularization_checklist,
        receives_public_support: formData.receives_public_support,
        public_support_types: formData.public_support_types,
        public_support_secretary: formData.public_support_secretary,
        has_active_agreement: formData.has_active_agreement,
        agreement_period_start: formData.agreement_period_start,
        agreement_period_end: formData.agreement_period_end
      };

      await api.patch(`/org-projects/${orgId}`, payload);

      toast.success('Organização atualizada com sucesso!');
      navigate(`/social/${orgId}`);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-lg">
          <Heart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600 mb-6">Você precisa estar logado para editar.</p>
          <button
            onClick={() => navigate('/perfil')}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Breadcrumbs 
            items={[
              { label: 'Impacto Social', path: '/social' },
              { label: org?.name || 'Organização', path: `/social/${orgId}` },
              { label: 'Editar', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Editar Organização</h1>
                <p className="text-purple-100 text-sm">{org?.name}</p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition-all disabled:opacity-50"
              data-testid="save-org-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Salvar
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tab: Eventos e Ações */}
        {activeTab === 'atividades' && org && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <OrgAtividadesTab orgId={org.id} />
          </div>
        )}

        {/* Tab: Info */}
        {activeTab === 'info' && (
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Informações Básicas
            </h2>

            <FormField label="Nome da organização" required>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </FormField>

            <FormField label="Descrição curta" required hint={`${formData.short_description.length}/250`}>
              <textarea
                value={formData.short_description}
                onChange={(e) => updateField('short_description', e.target.value.slice(0, 250))}
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
              />
            </FormField>

            <FormField label="Descrição detalhada" hint="Conte mais sobre a história, missão e atividades">
              <textarea
                value={formData.long_description}
                onChange={(e) => updateField('long_description', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
              />
            </FormField>

            <div className="border-t pt-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Phone className="w-5 h-5 text-purple-600" />
                Contato
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="WhatsApp / Telefone de contato">
                  <input
                    type="text"
                    value={formData.whatsapp}
                    onChange={(e) => updateField('whatsapp', e.target.value)}
                    placeholder="31999999999"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    data-testid="input-whatsapp"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!formData.is_whatsapp}
                      onChange={(e) => updateField('is_whatsapp', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      data-testid="checkbox-is-whatsapp"
                    />
                    <span className="text-sm text-gray-700">
                      Esse número é WhatsApp
                    </span>
                  </label>
                </FormField>

                <FormField label="E-mail">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="contato@org.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </FormField>

                <FormField label="Instagram">
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => updateField('instagram', e.target.value)}
                    placeholder="@organização"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </FormField>

                <FormField label="Site">
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://www.org.com.br"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </FormField>
              </div>
            </div>

            {/* Vídeo institucional */}
            <div className="border-t pt-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                Vídeo institucional (YouTube)
              </h3>
              <FormField label="Link do YouTube (opcional)">
                <input
                  type="url"
                  value={formData.video_institucional_url}
                  onChange={(e) => updateField('video_institucional_url', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  data-testid="input-video-institucional"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cole um link do YouTube (youtube.com/watch?v=... ou youtu.be/...).
                  Exibido no perfil público logo após a descrição.
                </p>
              </FormField>
              {(() => {
                const vid = extractYouTubeId(formData.video_institucional_url);
                if (!vid) {
                  if (formData.video_institucional_url) {
                    return (
                      <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Link inválido — use um endereço do YouTube.
                      </p>
                    );
                  }
                  return null;
                }
                return (
                  <div
                    className="mt-3 aspect-video rounded-xl overflow-hidden border border-gray-200 bg-black max-w-2xl"
                    data-testid="preview-video-institucional"
                  >
                    <iframe
                      title="Preview vídeo institucional"
                      src={`https://www.youtube.com/embed/${vid}`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Tab: Support Options */}
        {activeTab === 'support' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Heart className="w-5 h-5 text-purple-600" />
                Como as pessoas podem apoiar?
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Configure as formas de apoio disponíveis para sua organização
              </p>

              <div className="space-y-4">
                {/* Donate Items */}
                <SupportOptionCard
                  icon={Gift}
                  title="Doar Itens"
                  color="orange"
                  enabled={formData.support_options.donate_items.enabled}
                  onToggle={() => toggleSupportOption('donate_items')}
                >
                  <FormField label="Itens aceitos">
                    <input
                      type="text"
                      value={formData.support_options.donate_items.items_accepted}
                      onChange={(e) => updateSupportOption('donate_items', 'items_accepted', e.target.value)}
                      placeholder="Ex: Alimentos, roupas, brinquedos..."
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </FormField>
                  <FormField label="Instruções de entrega">
                    <textarea
                      value={formData.support_options.donate_items.instructions}
                      onChange={(e) => updateSupportOption('donate_items', 'instructions', e.target.value)}
                      placeholder="Ex: Entregas de segunda a sexta, das 9h às 17h..."
                      rows={2}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
                    />
                  </FormField>
                </SupportOptionCard>

                {/* Donate Money */}
                <SupportOptionCard
                  icon={DollarSign}
                  title="Doar em Dinheiro"
                  color="green"
                  enabled={formData.support_options.donate_money.enabled}
                  onToggle={() => toggleSupportOption('donate_money')}
                >
                  <div className="p-3 bg-yellow-50 rounded-lg mb-4">
                    <p className="text-xs text-yellow-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      Organizações verificadas têm maior credibilidade para doações em dinheiro
                    </p>
                  </div>
                  <FormField label="Instruções / Chave PIX">
                    <textarea
                      value={formData.support_options.donate_money.instructions}
                      onChange={(e) => updateSupportOption('donate_money', 'instructions', e.target.value)}
                      placeholder="Ex: PIX: 12.345.678/0001-90 ou instruções..."
                      rows={2}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 resize-none"
                    />
                  </FormField>
                  <FormField label="Link para página de doação">
                    <input
                      type="url"
                      value={formData.support_options.donate_money.link}
                      onChange={(e) => updateSupportOption('donate_money', 'link', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </FormField>
                </SupportOptionCard>

                {/* Volunteer */}
                <SupportOptionCard
                  icon={Users}
                  title="Ser Voluntário"
                  color="blue"
                  enabled={formData.support_options.volunteer.enabled}
                  onToggle={() => toggleSupportOption('volunteer')}
                >
                  <FormField label="Instruções / Áreas que precisam de voluntários">
                    <textarea
                      value={formData.support_options.volunteer.instructions}
                      onChange={(e) => updateSupportOption('volunteer', 'instructions', e.target.value)}
                      placeholder="Ex: Precisamos de voluntários para eventos, aulas de reforço..."
                      rows={3}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                    />
                  </FormField>
                </SupportOptionCard>

                {/* Participate */}
                <SupportOptionCard
                  icon={Calendar}
                  title="Participar"
                  color="purple"
                  enabled={formData.support_options.participate.enabled}
                  onToggle={() => toggleSupportOption('participate')}
                >
                  <FormField label="Instruções">
                    <textarea
                      value={formData.support_options.participate.instructions}
                      onChange={(e) => updateSupportOption('participate', 'instructions', e.target.value)}
                      placeholder="Ex: Participe de nossas atividades abertas ao público..."
                      rows={2}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
                    />
                  </FormField>
                  <FormField label="Link de inscrição">
                    <input
                      type="url"
                      value={formData.support_options.participate.link}
                      onChange={(e) => updateSupportOption('participate', 'link', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    />
                  </FormField>
                </SupportOptionCard>

                {/* Partnership */}
                <SupportOptionCard
                  icon={Handshake}
                  title="Parcerias"
                  color="teal"
                  enabled={formData.support_options.partnership.enabled}
                  onToggle={() => toggleSupportOption('partnership')}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField label="WhatsApp para parcerias">
                      <input
                        type="text"
                        value={formData.support_options.partnership.contact_whatsapp}
                        onChange={(e) => updateSupportOption('partnership', 'contact_whatsapp', e.target.value)}
                        placeholder="31999999999"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </FormField>
                    <FormField label="E-mail para parcerias">
                      <input
                        type="email"
                        value={formData.support_options.partnership.contact_email}
                        onChange={(e) => updateSupportOption('partnership', 'contact_email', e.target.value)}
                        placeholder="parcerias@org.com"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </FormField>
                  </div>
                </SupportOptionCard>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Impact Metrics */}
        {activeTab === 'impact' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Métricas de Impacto
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Dados autodeclarados sobre o impacto da sua organização
            </p>

            <div className="p-4 bg-purple-50 rounded-xl mb-6">
              <p className="text-sm text-purple-700 flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Esses dados serão exibidos como &quot;autodeclarados&quot; no perfil público da organização
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <FormField label="Pessoas impactadas (último mês)">
                <SelectField
                  value={formData.impact_metrics.people_impacted_last_month}
                  onChange={(v) => updateImpactMetric('people_impacted_last_month', v)}
                  options={IMPACT_RANGES_MONTH}
                  placeholder="Selecione uma faixa..."
                />
              </FormField>

              <FormField label="Pessoas impactadas (último ano)">
                <SelectField
                  value={formData.impact_metrics.people_impacted_last_year}
                  onChange={(v) => updateImpactMetric('people_impacted_last_year', v)}
                  options={IMPACT_RANGES_YEAR}
                  placeholder="Selecione uma faixa..."
                />
              </FormField>

              <FormField label="Famílias atendidas">
                <input
                  type="text"
                  value={formData.impact_metrics.families_impacted}
                  onChange={(e) => updateImpactMetric('families_impacted', e.target.value)}
                  placeholder="Ex: Cerca de 50 famílias"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </FormField>

              <FormField label="Serviços prestados">
                <input
                  type="text"
                  value={formData.impact_metrics.services_delivered}
                  onChange={(e) => updateImpactMetric('services_delivered', e.target.value)}
                  placeholder="Ex: 200 cestas básicas, 50 atendimentos..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </FormField>
            </div>

            <FormField label="Como vocês medem o impacto?" hint="Descreva brevemente sua metodologia">
              <textarea
                value={formData.impact_metrics.measurement_method}
                onChange={(e) => updateImpactMetric('measurement_method', e.target.value)}
                placeholder="Ex: Contagem de atendimentos mensais, questionários de feedback..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
              />
            </FormField>
          </div>
        )}

        {/* Tab: Governance */}
        {activeTab === 'governance' && (
          <div className="space-y-6">
            {/* Regularization Checklist */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Checklist de Regularização
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Marque os itens que sua organização possui em dia
              </p>

              <div className="space-y-3">
                {REGULARIZATION_CHECKLIST_ITEMS.map(item => (
                  <label
                    key={item.key}
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      formData.regularization_checklist?.[item.key]
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.regularization_checklist?.[item.key] || false}
                      onChange={(e) => updateChecklistItem(item.key, e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className={`text-sm ${
                      formData.regularization_checklist?.[item.key] ? 'text-green-800' : 'text-gray-700'
                    }`}>
                      {item.label}
                    </span>
                    {formData.regularization_checklist?.[item.key] && (
                      <CheckCircle className="w-5 h-5 text-green-600 ml-auto" />
                    )}
                  </label>
                ))}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-700 flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Organizações com documentação completa têm mais chances de estabelecer 
                    parcerias e receber apoio do poder público.
                  </span>
                </p>
              </div>
            </div>

            {/* Public Support */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-indigo-600" />
                Relação com o Poder Público
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Informe se sua organização recebe apoio de órgãos públicos
              </p>

              {/* Receives Support Toggle */}
              <div className="mb-6">
                <label className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                  formData.receives_public_support
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}>
                  <button
                    type="button"
                    onClick={() => updateField('receives_public_support', !formData.receives_public_support)}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      formData.receives_public_support ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        formData.receives_public_support ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                  <div>
                    <p className="font-medium text-gray-900">Recebe apoio do poder público</p>
                    <p className="text-sm text-gray-500">Financeiro, cessão de espaço, materiais, etc.</p>
                  </div>
                </label>
              </div>

              {formData.receives_public_support && (
                <div className="space-y-4 pl-4 border-l-2 border-indigo-200">
                  {/* Support Types */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Tipos de apoio recebido</h4>
                    <div className="flex flex-wrap gap-2">
                      {PUBLIC_SUPPORT_TYPES.map(type => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => togglePublicSupportType(type.value)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            formData.public_support_types?.includes(type.value)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-indigo-100'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Secretary */}
                  <FormField label="Secretaria/Órgão relacionado">
                    <SelectField
                      value={formData.public_support_secretary}
                      onChange={(v) => updateField('public_support_secretary', v)}
                      options={SECRETARIES}
                      placeholder="Selecione a secretaria..."
                    />
                  </FormField>
                </div>
              )}
            </div>

            {/* Active Agreement */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Handshake className="w-5 h-5 text-green-600" />
                Convênio / Termo de Parceria
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Informe se existe convênio vigente com o poder público
              </p>

              <div className="mb-6">
                <label className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                  formData.has_active_agreement
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}>
                  <button
                    type="button"
                    onClick={() => updateField('has_active_agreement', !formData.has_active_agreement)}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      formData.has_active_agreement ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        formData.has_active_agreement ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                  <div>
                    <p className="font-medium text-gray-900">Possui convênio/termo vigente</p>
                    <p className="text-sm text-gray-500">Convênio, termo de fomento ou colaboração</p>
                  </div>
                </label>
              </div>

              {formData.has_active_agreement && (
                <div className="grid md:grid-cols-2 gap-4 pl-4 border-l-2 border-green-200">
                  <FormField label="Início do período">
                    <input
                      type="date"
                      value={formData.agreement_period_start}
                      onChange={(e) => updateField('agreement_period_start', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </FormField>
                  <FormField label="Fim do período">
                    <input
                      type="date"
                      value={formData.agreement_period_end}
                      onChange={(e) => updateField('agreement_period_end', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </FormField>
                </div>
              )}
            </div>

            {/* Transferência de responsável (iter 204) */}
            <TransferirResponsavelCard
              orgId={orgId}
              currentResponsavel={{
                id: org?.responsavel_user_id || org?.owner_id,
                email: org?.owner_email,
                nome: org?.responsavel_nome,
              }}
              onTransferred={(novo) => {
                toast.success(`Responsabilidade transferida para ${novo.email}. Você será deslogado da edição.`);
                setTimeout(() => navigate('/painel'), 2000);
              }}
              onConviteCriado={() => setConvitesRefreshTick((n) => n + 1)}
            />

            {/* Convites pendentes (iter 207) */}
            <ConvitesPendentesCard
              key={convitesRefreshTick}
              orgId={orgId}
              onTransferred={(novo) => {
                toast.success(`Responsabilidade transferida para ${novo.email}.`);
                setTimeout(() => navigate('/painel'), 2000);
              }}
            />
          </div>
        )}

        {/* Tab: Media */}
        {activeTab === 'media' && (
          <div className="space-y-6">
            {/* Logo / Avatar */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Camera className="w-5 h-5 text-purple-600" />
                Logo / Avatar
              </h2>

              <ImageUploader
                area="ong"
                tipo="logo"
                entidadeId={orgId}
                municipioId={formData.municipio_id || org?.municipio_id}
                imagensAtuais={
                  formData.imagens?.logo
                    ? formData.imagens.logo
                    : (formData.avatar_url
                        ? { thumb: formData.avatar_url, medium: formData.avatar_url, large: formData.avatar_url, legacy: true }
                        : null)
                }
                single
                label="Logo da organização"
                description="Imagem quadrada (ex: 400x400px) — exibida no perfil da ONG"
                onChange={(imgs) => {
                  const next = imgs[0] || null;
                  setFormData((prev) => ({
                    ...prev,
                    imagens: { ...prev.imagens, logo: next },
                    avatar_url: imgs.length === 0 ? '' : (next?.medium || prev.avatar_url),
                  }));
                }}
              />
            </div>

            {/* Galeria */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                Galeria de fotos
              </h2>

              <ImageUploader
                area="ong"
                tipo="galeria"
                entidadeId={orgId}
                municipioId={formData.municipio_id || org?.municipio_id}
                imagensAtuais={formData.imagens?.galeria || []}
                label="Fotos da organização"
                description="Adicione fotos das atividades, sede, equipe e eventos da organização"
                onChange={(imgs) => {
                  setFormData((prev) => ({
                    ...prev,
                    imagens: { ...prev.imagens, galeria: imgs },
                  }));
                }}
              />
            </div>

            {/* Empresas parceiras */}
            <ParceirosSection
              orgId={orgId}
              municipioId={formData.municipio_id || org?.municipio_id}
              initialItems={(org?.supporters || []).filter(
                (s) => s.supporter_type === 'empresa',
              )}
            />
          </div>
        )}

        {/* Save Button (Mobile) */}
        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Salvar Alterações
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
